"use strict"

import React from "react"
import { FormattedMessage, useIntl } from "react-intl"

import { checkIfDuplicateExists } from "../../widgets/utils"
import { ConfirmEditedResource } from "./ModalParts"
import EditResourceModal, { DisabledTextInput, ConditionalTagTextInput } from "./EditResourceModal"

/*
NOTE: Disabling all form fields - Except active/inactive toggle

Disabling all form fields in the UI (for now). Things get complicated when column/tag/unit
registrations are managed by our source_tag_registration.json files and changes are ALSO allowed in the UI.
This can lead to tag registrations (which are shared between LanzaLake and LTPM) getting out of sync and
changes made in the UI could be overwritten by a bootstrap command if the .json was not also updated with
new assignments. While tag registrations are managed by .json files, best not to introduce this potential
outlet for error introduction.

All tag changes must be made by a developer by updating the associated .json file(s) and rerunning the
bootstrap command.
*/

const disable_fields = true // DONT set to 'false' until the above has been fully addressed

export default function EditDCSTagsModal(props) {
  const intl = useIntl()
  const { userCanEditTags, table } = props
  const editingDisabled = !!(!userCanEditTags || table.pseudo_table)

  const table_info = [
    {
      header_id: "general.source",
      key: "source",
      style: (tag) => {
        return ""
      },
      content: (tag, idx, updateFormValue, list) => {
        // Never need to edit this here
        return <DisabledTextInput value={tag.source} />
      },
    },
    {
      header_id: "reactormanagement.column",
      key: "column",
      style: (tag) => {
        return ""
      },
      content: (tag, idx, updateFormValue, list) => {
        // Never need to edit this here
        return <DisabledTextInput value={tag.column} />
      },
    },
    {
      header_id: "reactormanagement.name",
      key: "name",
      style: (tag) => {
        return ""
      },
      content: (tag, idx, updateFormValue, list) => {
        if (disable_fields || editingDisabled) {
          // Leave passing true (`disable_fields`) alongside other checks (see note)
          return <DisabledTextInput value={tag["name"]} />
        } else {
          return (
            <ConditionalTagTextInput tag={tag} idx={idx} value={"name"} updateFormValue={updateFormValue} />
          )
        }
      },
    },
    {
      header_id: "reactormanagement.pandid",
      key: "p_and_id",
      style: (tag) => {
        return ""
      },
      content: (tag, idx, updateFormValue, list) => {
        if (disable_fields || editingDisabled) {
          // Leave passing true (`disable_fields`) alongside other checks (see note)
          return <DisabledTextInput value={tag["p_and_id"]} />
        } else {
          return (
            <ConditionalTagTextInput
              tag={tag}
              idx={idx}
              value={"p_and_id"}
              updateFormValue={updateFormValue}
            />
          )
        }
      },
    },
    {
      header_id: "general.tag",
      key: "tag",
      style: (tag) => {
        return ""
      },
      content: (tag, idx, updateFormValue, list) => {
        const mapped_tags_list = list.filter((t) => t.tag).map((t) => t.tag)
        return (
          <select
            className={"form-select form-select-sm"}
            name={"tag"}
            value={tag.tag || ""}
            disabled={disable_fields || !tag.active || editingDisabled} // Leave passing true (`disable_fields`) alongside other checks (see note)
            onChange={(e) => updateFormValue(e, "tag", e.target.value, idx, tag.warning)}
          >
            <option value="">{intl.formatMessage({ id: "general.dropdown.please.select" })}</option>
            {tag.tag && <option value={tag.tag}>{tag.tag}</option>}
            {props.tags
              .filter((t) => !mapped_tags_list.includes(t.tag))
              .map((t, idx) => {
                return (
                  <option key={idx} value={t.tag}>
                    {t.tag}
                  </option>
                )
              })}
          </select>
        )
      },
    },
    {
      header_id: "reactormanagement.datatype",
      key: "data_type",
      style: (tag) => {
        return ""
      },
      content: (tag, idx, updateFormValue, list) => {
        return (
          <select
            className={"form-select form-select-sm"}
            name={"data_type"}
            value={tag["data_type"] || ""}
            disabled={disable_fields || !tag.active || editingDisabled} // Leave passing true (`disable_fields`) alongside other checks (see note)
            onChange={(e) => updateFormValue(e, "data_type", e.target.value, idx, tag.warning)}
          >
            <option value="">{intl.formatMessage({ id: "general.dropdown.please.select" })}</option>
            {props.data_types.map((ot, idx) => {
              return (
                <option key={idx} value={ot}>
                  {ot}
                </option>
              )
            })}
          </select>
        )
      },
    },
    {
      header_id: "general.active",
      key: "active",
      style: (tag) => {
        return ""
      },
      content: (tag, idx, updateFormValue, list) => {
        return (
          <input
            type={"checkbox"}
            key={Math.random()} // To force rerender of checkbox on change
            checked={tag.active}
            disabled={editingDisabled}
            onChange={(e) => updateFormValue(e, "active", !tag.active, idx, tag.warning)}
          />
        )
      },
    },
  ]

  return (
    <EditResourceModal
      editable_fields={["active", "name", "p_and_id", "tag", "data_type"]}
      fields_to_complete={["tag", "data_type"]}
      fields_to_search={["column", "name", "p_and_id", "tag"]}
      resources={table}
      step_into_resources={"tags"}
      table_title={
        <h5>
          <FormattedMessage id={"reactormanagement.dcstable"} description={"Edit DCSTags"} />: {table.name}
        </h5>
      }
      additionalValidationCheck={(validSubmission, edited_tags, editTags, updateFormValue) => {
        const tags_list = editTags.filter((tag) => tag.tag !== "" && tag.tag)
        const tagDuplicates = checkIfDuplicateExists(tags_list.map((tag) => tag.tag))
        editTags.forEach((tag, idx) => {
          if (tag.edited && tag.complete) {
            if (tagDuplicates && tags_list.filter((t) => t.tag === tag.tag).length > 1) {
              validSubmission = false
              updateFormValue(false, "warning", true, idx, tag.warning)
            } else if (tag.warning) {
              updateFormValue(false, "warning", false, idx, tag.warning)
            }
          }
        })
        return validSubmission
      }}
      submitData={(data, loaderRef) => {
        props.submitData(data, loaderRef, table.id)
      }}
      ModalPart={ConfirmEditedResource}
      modal_title_id={"navigation.source.registration"}
      resource_name={"Edit DCSTags"}
      table_info={table_info}
      table_row_style={(tag) => {
        if (tag.edited && tag.complete && !tag.warning) {
          return "table_edited"
        } else if (tag.warning || !tag.complete) {
          return "table_warned"
        } else {
          return "table_okay"
        }
      }}
      initial_tag_state={(tag) => {
        return {
          source: tag["source"],
          edited: false,
          complete: false,
          warning: false,
        }
      }}
      warning={props.warning}
      show={props.show}
      onHide={props.onHide}
      readOnly={editingDisabled || props.warning}
    />
  )
}
